'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { CompactProductFragmentFragmentDoc } from '@packages/gql/generated/shopping/CompactProductFragmentFragmentDoc';
import { Price } from '@packages/modules/src/Price/Price';
import { useResponsiveAkamaiTemplate } from '@packages/modules/src/hooks/useResponsiveAkamaiTemplate/useResponsiveAkamaiTemplate';
import { TaxHint } from '../TaxHint';

/* GraphQL */ `
  fragment CompactProductFragment on Product {
    name
    price {
      ...ProductPriceFragment
    }
    media {
      type
      url
    }
    delivery {
      ...TaxHintFragment
    }
  }
`;

export type CompactProductProps = {
  /**
   * Price, media and deliveryType **data** from given product.
   */
  data: FragmentType<typeof CompactProductFragmentFragmentDoc>;
  /**
   * Whether to show product name
   * Shows maximal two lines of the name
   * @default false
   */
  withProductName?: boolean;

  /**
   * Whether to show tax hint
   * @default true
   */
  showTaxHint?: boolean;
};

/**
 * CompactProduct component displays the Price and Image of a given product in a condensed layout.
 * */
export const CompactProduct = ({
  data: compactProductData,
  withProductName = false,
  showTaxHint = true,
}: CompactProductProps) => {
  const { media, name, price, delivery } = getFragmentData(
    CompactProductFragmentFragmentDoc,
    compactProductData,
  );
  const { templateName, imageDimensions } = useResponsiveAkamaiTemplate('compact');

  /**
   * URL of the first image in the media array.
   * TODO: check if showing the 1st image is the expected behavior -> https://empiriecom.atlassian.net/browse/INFORM-2593
   */
  const imageUrl = media.find((ele) => ele.type === 'IMAGE')?.url;

  return (
    <div className="flex flex-row items-start gap-1.5">
      <div className="relative h-[72px] w-[72px] lg:h-[88px] lg:w-[88px]">
        {imageUrl && (
          <Image
            role="presentation"
            src={`${imageUrl}?${templateName}`}
            unoptimized
            alt=""
            style={{
              objectFit: 'contain',
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
            }}
            {...imageDimensions}
          />
        )}
      </div>
      <div>
        {withProductName && (
          <p className="text-size-h4 text-color-text-dark mb-0.5 line-clamp-2 block font-bold">
            {name}
          </p>
        )}
        <Price data={price} />
        {showTaxHint && <TaxHint data={delivery} />}
      </div>
    </div>
  );
};
